import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Container = styled.div`
  min-height: calc(100vh - 200px);
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 2rem 0;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #efb348;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Section = styled.section`
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;

  h2 {
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    color: #efb348;
    font-size: 1.5rem;
    border-bottom: 2px solid #efb348;
  }

  p {
    margin-bottom: 1.5rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.8;
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    margin-top: 1rem;
    a {
      color: #42a5f5;
      font-weight: bold;
      text-decoration: none;
      &:hover {
        color: #81d4fa;
        text-decoration: underline;
      }
    }
  }
`;

const CryptoTokenIntroduction = () => {
  const { t } = useTranslation();

  // Scroll to anchor on mount and hash change
  useEffect(() => {
    const scrollToHash = () => {
      if (window.location.hash) {
        const id = window.location.hash.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  const sections = [1, 2, 3, 4, 5].map(i => ({
    subtitle: t(`cryptoToken.sections.${i}.subtitle`),
    text: t(`cryptoToken.sections.${i}.text`),
    internal: t(`cryptoToken.sections.${i}.internalLink`),
    external: t(`cryptoToken.sections.${i}.externalLink`),
    internalLabel: t(`cryptoToken.sections.${i}.internalLabel`),
    externalLabel: t(`cryptoToken.sections.${i}.externalLabel`),
    icon: t(`cryptoToken.sections.${i}.icon`),
  }));

  return (
    <Container>
      <ContentWrapper>
        <PageTitle>{t('cryptoToken.title')}</PageTitle>
        {sections.map((section, idx) => (
          <Section key={idx} id={section.icon.replace(/\.[^/.]+$/, '')}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={`/images/icons/${section.icon}`}
                alt={section.subtitle}
                style={{ width: 32, height: 32, verticalAlign: 'middle' }}
              />
              {section.subtitle}
            </h2>
            <p>{section.text}</p>
            <div className="links">
              <a
                href={section.internal}
                target="_blank"
                rel="noopener noreferrer"
              >
                {section.internalLabel || t('cryptoToken.internalDefault')}
              </a>
              <a
                href={section.external}
                target="_blank"
                rel="noopener noreferrer"
              >
                {section.externalLabel || t('cryptoToken.externalDefault')}
              </a>
            </div>
          </Section>
        ))}
      </ContentWrapper>
    </Container>
  );
};

export default CryptoTokenIntroduction;
